import React from 'react';
import { Wallet, TrendingUp, TrendingDown, Scale, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { formatMontant } from './nosDepensesUtils';
import { useFeatures } from '../../contexts/FeaturesContext';

// Cartes de statistiques calquees sur MaCaisse : fond pastel + border-2,
// pastille d'icone, puis encadre blanc interne avec lignes et ligne de total.
const CentralStatsCards = ({
  soldeReelCentral, entreesCentrale, sortiesCentrale, soldeAValider, totalTransactions,
  totalEncaissements, totalDepenses,
  caissesEnAttente, caissesValidees, caissesAvecObservation,
  remisesValidees, dotationsSorties, salairesCentraux, grossesDepenses,
  isLoadingStats
}) => {
  // Les salaires centraux relevent de la paie, vendue separement de la
  // comptabilite. On garde neanmoins la ligne si elle porte un montant : elle
  // entre dans « Total sorties », et la masquer donnerait un total qui ne
  // s'additionne plus sous les yeux du lecteur.
  const { estActive } = useFeatures();
  const salairesVisibles = estActive('module.paie') || Number(salairesCentraux || 0) !== 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 px-6">
      {/* Entrées validées */}
      <Card className="bg-emerald-50 border-2 border-emerald-200">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 bg-emerald-100 rounded-lg flex items-center justify-center">
              <TrendingUp size={22} className="text-emerald-600" />
            </div>
            {isLoadingStats && (
              <Loader2 size={16} className="animate-spin text-emerald-600" />
            )}
          </div>
          <p className="text-gray-600 text-sm mb-3 font-semibold">Entrées validées</p>
          <div className="space-y-2 bg-white rounded-lg p-3 border border-emerald-100">
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-600">Remises validées</span>
              <span className="font-semibold text-emerald-600 text-right">{formatMontant(remisesValidees)}</span>
            </div>
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-600">Encaissements période</span>
              <span className="font-semibold text-blue-600 text-right">{formatMontant(totalEncaissements)}</span>
            </div>
            <div className="border-t border-emerald-200 pt-2 mt-2 flex justify-between items-center gap-3">
              <span className="text-gray-700 font-semibold">Total entrées</span>
              <span className="text-xl font-bold text-emerald-600 text-right">{formatMontant(entreesCentrale)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sorties validées */}
      <Card className="bg-orange-50 border-2 border-orange-200">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 bg-orange-100 rounded-lg flex items-center justify-center">
              <TrendingDown size={22} className="text-orange-600" />
            </div>
            {isLoadingStats && (
              <Loader2 size={16} className="animate-spin text-orange-600" />
            )}
          </div>
          <p className="text-gray-600 text-sm mb-3 font-semibold">Sorties validées</p>
          <div className="space-y-2 bg-white rounded-lg p-3 border border-orange-100">
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-600">Dotations versées</span>
              <span className="font-semibold text-[#319190] text-right">{formatMontant(dotationsSorties)}</span>
            </div>
            {salairesVisibles && (
              <div className="flex justify-between items-center gap-3 text-sm">
                <span className="text-gray-600">Salaires centraux</span>
                <span className="font-semibold text-amber-600 text-right">{formatMontant(salairesCentraux)}</span>
              </div>
            )}
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-600">Grosses dépenses</span>
              <span className="font-semibold text-red-600 text-right">{formatMontant(grossesDepenses)}</span>
            </div>
            <div className="border-t border-orange-200 pt-2 mt-2 flex justify-between items-center gap-3">
              <span className="text-gray-700 font-semibold">Total sorties</span>
              <span className="text-xl font-bold text-orange-600 text-right">{formatMontant(sortiesCentrale)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contrôle des caisses */}
      <Card className="bg-blue-50 border-2 border-blue-200">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 bg-blue-100 rounded-lg flex items-center justify-center">
              <Wallet size={22} className="text-blue-600" />
            </div>
            {isLoadingStats && (
              <Loader2 size={16} className="animate-spin text-blue-600" />
            )}
          </div>
          <p className="text-gray-600 text-sm mb-3 font-semibold">Contrôle des caisses</p>
          <div className="space-y-2 bg-white rounded-lg p-3 border border-blue-100">
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-600">En attente de validation</span>
              <span className="font-semibold text-yellow-600 text-right">{caissesEnAttente}</span>
            </div>
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-600">Avec observation</span>
              <span className="font-semibold text-orange-600 text-right">{caissesAvecObservation}</span>
            </div>
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-600">Déjà validées</span>
              <span className="font-semibold text-emerald-600 text-right">{caissesValidees}</span>
            </div>
            <div className="border-t border-blue-200 pt-2 mt-2 flex justify-between items-center gap-3">
              <span className="text-gray-700 font-semibold">Montant à valider</span>
              <span className="text-xl font-bold text-blue-700 text-right">{formatMontant(soldeAValider)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Solde réel central */}
      <Card className="bg-[#319190]/10 border-2 border-[#319190]/25">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 bg-[#319190]/15 rounded-lg flex items-center justify-center">
              <Scale size={22} className="text-[#319190]" />
            </div>
            {isLoadingStats && (
              <Loader2 size={16} className="animate-spin text-[#246990]" />
            )}
          </div>
          <p className="text-gray-600 text-sm mb-3 font-semibold">Solde réel central</p>
          <div className="space-y-2 bg-white rounded-lg p-3 border border-[#319190]/15">
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-600">Entrées validées</span>
              <span className="font-semibold text-emerald-600 text-right">+{formatMontant(entreesCentrale)}</span>
            </div>
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-600">Sorties validées</span>
              <span className="font-semibold text-red-600 text-right">-{formatMontant(sortiesCentrale)}</span>
            </div>
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-600">Transactions</span>
              <span className="font-semibold text-gray-700 text-right">{totalTransactions}</span>
            </div>
            <div className="border-t border-[#319190]/25 pt-2 mt-2">
              <div className="flex justify-between items-center gap-3">
                <span className="text-gray-700 font-semibold">Solde disponible</span>
                <span className={soldeReelCentral >= 0 ? 'text-xl font-bold text-[#319190] text-right' : 'text-xl font-bold text-red-600 text-right'}>
                  {formatMontant(soldeReelCentral)}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-2">Mouvements officiellement validés en caisse centrale uniquement.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CentralStatsCards;
